<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractInvoicing',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const updateBillingCycle = (...args) => {
    props.contract.invoicing.billing_cycle = `${billing_frequency.value.value} ${billing_frequency.value.unit}`;
  };

  const currencies = ref(null);
  const billing_frequency = ref(null);
  const billingFrequencyOptions = ref([
    {
      value: 'day',
      text: $fn.value.gettext('day'),
    },
    {
      value: 'month',
      text: $fn.value.gettext('month'),
    },
  ]);
  const contractPaymentTermTypes = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    currencies: currencies.value,
    billing_frequency: billing_frequency.value,
    billingFrequencyOptions: billingFrequencyOptions.value,
    contractPaymentTermTypes: contractPaymentTermTypes.value,
    ux: ux.value,
  }));

  const $$load_currencies = () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_billing_frequency = () => {
    return props.contract.invoicing.billing_cycle != null
      ? {
          value: props.contract.invoicing.billing_cycle.split(' ')[0],
          unit: props.contract.invoicing.billing_cycle.split(' ')[1],
        }
      : {
          value: null,
          unit: null,
        };
  };
  const $$load_contractPaymentTermTypes = () => {
    return $fn.value.getEnumValuesFromSchema('ContractPaymentTermTypes');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'currencies' },
      { name: 'billing_frequency' },
      { name: 'billingFrequencyOptions' },
      { name: 'contractPaymentTermTypes' },
      { name: 'ux' },
    ],
    loaders: {
      currencies: $$load_currencies,
      billing_frequency: $$load_billing_frequency,
      contractPaymentTermTypes: $$load_contractPaymentTermTypes,
    },
    ux: {},
    dataRefs: {
      currencies,
      billing_frequency,
      billingFrequencyOptions,
      contractPaymentTermTypes,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_1485536987289644_script_5yu0n0zwh_value_callable = async (
    $event
  ) => {
    return updateBillingCycle();
  };
  const act_1485536987289644_script_5yu0n0zwh_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1485536987289644_script_5yu0n0zwh_value_callable($event);
      },
    };
  };
  const act_8376657076005968_script_5bb3cgr0u_value_callable = async (
    $event
  ) => {
    return updateBillingCycle();
  };
  const act_8376657076005968_script_5bb3cgr0u_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8376657076005968_script_5bb3cgr0u_value_callable($event);
      },
    };
  };

  const act_1485536987289644_script_5yu0n0zwh = new actionTypes.ScriptAction({
    actionArgs: act_1485536987289644_script_5yu0n0zwh_getActionArgs,
  });
  const inst_evh_1485536987289644_input_0125jrg0a =
    new actionTypes.EventHandlerImpl({
      action: act_1485536987289644_script_5yu0n0zwh,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_1485536987289644_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1485536987289644_input_0125jrg0a],
    event: 'input',
  });
  const act_8376657076005968_script_5bb3cgr0u = new actionTypes.ScriptAction({
    actionArgs: act_8376657076005968_script_5bb3cgr0u_getActionArgs,
  });
  const inst_evh_8376657076005968_input_j9dygz6pu =
    new actionTypes.EventHandlerImpl({
      action: act_8376657076005968_script_5bb3cgr0u,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_8376657076005968_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8376657076005968_input_j9dygz6pu],
    event: 'input',
  });

  const actions = {
    inst_evh_1485536987289644_input_0125jrg0a,
    inst_evh_1485536987289644_input,
    inst_evh_8376657076005968_input_j9dygz6pu,
    inst_evh_8376657076005968_input,
  };

  const evh_1485536987289644_input_0125jrg0a = async (event, scope) => {
    return await actions.inst_evh_1485536987289644_input_0125jrg0a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1485536987289644_input = async (event, scope) => {
    return await actions.inst_evh_1485536987289644_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8376657076005968_input_j9dygz6pu = async (event, scope) => {
    return await actions.inst_evh_8376657076005968_input_j9dygz6pu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8376657076005968_input = async (event, scope) => {
    return await actions.inst_evh_8376657076005968_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpFormGroup
    name="rental_fee_advances_initial_months"
    :label="pgettext('Contract', 'Initial advances')"
    :appendText="pgettext('InputAddon', 'months')"
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
  >
    <ItpFormInput
      type="number"
      name="rental_fee_advances_initial_months"
      v-model="contract.invoicing.rental_fee_advances_initial_months"
    >
    </ItpFormInput>
  </ItpFormGroup>
  <ItpFormGroup
    name="rental_fee_advances_reserve_months"
    :label="pgettext('Contract', 'Reserve advances')"
    :appendText="pgettext('InputAddon', 'months')"
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
  >
    <ItpFormInput
      type="number"
      name="rental_fee_advances_reserve_months"
      v-model="contract.invoicing.rental_fee_advances_reserve_months"
    >
    </ItpFormInput>
  </ItpFormGroup>
  <hr />
  <ItpFormGroup
    name="billing_currency"
    :label="pgettext('Contract', 'Billing currency')"
    :label-cols-md="4"
    :content-cols-md="3"
    :labelAlign="labelAlign"
  >
    <ItpFormSearchableSelect
      name="billing_currency"
      valueField="code"
      textField="code"
      required
      v-model="contract.invoicing.billing_currency"
      :options="currencies"
    >
    </ItpFormSearchableSelect>
  </ItpFormGroup>
  <ItpFormGroup
    name="billing_frequency"
    :label="pgettext('Contract', 'Billing frequency')"
    :label-cols-md="4"
    :content-cols-md="6"
    :labelAlign="labelAlign"
    contentClass="input-group"
    v-if="loaded"
  >
    <ItpFormInput
      type="number"
      name="billing_frequency_value"
      v-model="billing_frequency.value"
      @input="evh_1485536987289644_input($event, {})"
    >
    </ItpFormInput>
    <ItpFormNativeSelect
      name="billing_frequency_unit"
      v-model="billing_frequency.unit"
      :options="billingFrequencyOptions"
      class="w-50"
      @input="evh_8376657076005968_input($event, {})"
    >
    </ItpFormNativeSelect>
  </ItpFormGroup>
  <ItpFormGroup
    name="payment_terms"
    :label="pgettext('Contract', 'Payment period')"
    :label-cols-md="4"
    :content-cols-md="6"
    :labelAlign="labelAlign"
    contentClass="input-group"
    v-if="contract.invoicing.payment_terms"
  >
    <ItpFormInput
      type="number"
      name="payment_terms_value"
      v-model="contract.invoicing.payment_terms.value"
    >
    </ItpFormInput>
    <ItpFormNativeSelect
      name="payment_terms_type"
      v-model="contract.invoicing.payment_terms.type"
      :options="contractPaymentTermTypes"
      class="w-50"
    >
    </ItpFormNativeSelect>
  </ItpFormGroup>
  <hr />
  <ItpFormGroup
    name="invoice_notes"
    :label="pgettext('Contract', 'Notes on invoice')"
    :label-cols-md="4"
    :content-cols-md="8"
    :labelAlign="labelAlign"
  >
    <ItpFormTextarea
      name="invoice_notes_value"
      v-model="contract.invoicing.invoice_notes"
    >
    </ItpFormTextarea>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_contract_number"
    :label="pgettext('Contract', 'Contract in invoice notes')"
    :helpTooltip="
      pgettext(
        'Contract',
        'Whether to include the contract number in the invoice notes when generating invoices.'
      )
    "
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_contract_number"
      v-model="contract.invoicing.invoice_notes_config.include_contract_number"
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_rental"
    :label="pgettext('Contract', 'Rental in invoice notes')"
    :helpTooltip="
      pgettext(
        'Contract',
        'Whether to include the rental name in the invoice notes when generating invoices.'
      )
    "
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_rental"
      v-model="contract.invoicing.invoice_notes_config.include_rental"
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_invoice_period"
    :label="pgettext('Contract', 'Period in invoice notes')"
    :helpTooltip="
      pgettext(
        'Contract',
        'Whether to include the billing period in the invoice notes when generating invoices.'
      )
    "
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_invoice_period"
      v-model="contract.invoicing.invoice_notes_config.include_invoice_period"
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
</template>
